import React, { useState } from "react";

function getDetailed2DMatrixSearchSteps(matrix, target) {
  const steps = [];
  const rows = matrix.length;
  const cols = matrix[0].length;
  let left = 0;
  let right = rows * cols - 1;

  function addStep(_left, _right, _mid, msg) {
    steps.push({ left: _left, right: _right, mid: _mid, message: msg });
  }

  addStep(left, right, null, `Initial boundaries set: left = ${left}, right = ${right}`);

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const midRow = Math.floor(mid / cols);
    const midCol = mid % cols;
    const midValue = matrix[midRow][midCol];

    addStep(left, right, mid, `Compute mid = (left + right) // 2 = ${mid}`);
    addStep(left, right, mid, `Convert mid => (row, col) = (${midRow}, ${midCol})`);
    addStep(left, right, mid, `Check matrix[${midRow}][${midCol}] = ${midValue}`);

    if (midValue === target) {
      addStep(left, right, mid, `Found target ${target} at [${midRow}][${midCol}].`);
      return steps;
    } else if (midValue < target) {
      addStep(left, right, mid, `Value ${midValue} < target ${target}; move left to mid + 1.`);
      left = mid + 1;
    } else {
      addStep(left, right, mid, `Value ${midValue} > target ${target}; move right to mid - 1.`);
      right = mid - 1;
    }
  }

  addStep(left, right, null, `Target ${target} not found in matrix.`);
  return steps;
}

const MatrixSearchAnimation = () => {
  const matrix = [
    [1, 3, 5, 7],
    [10, 11, 16, 20],
    [23, 30, 34, 60],
  ];
  const target = 19;
  const steps = getDetailed2DMatrixSearchSteps(matrix, target);
  const [stepIndex, setStepIndex] = useState(0);
  const currentStep = steps[stepIndex];
  const { left, right, mid, message } = currentStep;
  const rows = matrix.length;
  const cols = matrix[0].length;

  const getCellStyle = (row, col) => {
    const index = row * cols + col;
    if (index === mid) return "bg-green-600";
    if (index === left) return "bg-yellow-600";
    if (index === right) return "bg-pink-600";
    return "bg-gray-200";
  };

  const handlePrevStep = () => {
    if (stepIndex > 0) setStepIndex(stepIndex - 1);
  };

  const handleNextStep = () => {
    if (stepIndex < steps.length - 1) setStepIndex(stepIndex + 1);
  };

  return (
    <div className="p-4 bg-gray-100 dark:bg-gray-900">
      <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
        2D Matrix Search Animation (Detailed Steps)
      </h2>
      <div className="grid grid-cols-4 gap-4 mb-6">
        {matrix.map((rowArray, rowIndex) =>
          rowArray.map((val, colIndex) => (
            <div
              key={`${rowIndex}-${colIndex}`}
              className={`w-16 h-16 flex items-center justify-center rounded shadow text-black ${getCellStyle(rowIndex, colIndex)}`}
            >
              {val}
            </div>
          ))
        )}
      </div>
      <div className="mb-4 space-x-2">
        <button
          onClick={handlePrevStep}
          disabled={stepIndex === 0}
          className={`px-4 py-2 rounded text-white ${stepIndex > 0 ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400"}`}
        >
          Previous Step
        </button>
        <button
          onClick={handleNextStep}
          disabled={stepIndex === steps.length - 1}
          className={`px-4 py-2 rounded text-white ${stepIndex < steps.length - 1 ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400"}`}
        >
          Next Step
        </button>
      </div>
      <div className="bg-white dark:bg-gray-800 p-4 rounded-md shadow">
        <p className="text-gray-900 dark:text-gray-200">
          <strong>Step {stepIndex + 1} / {steps.length}:</strong> {message}
        </p>
        <p className="text-gray-500 dark:text-gray-400 mt-2">
          <strong>left:</strong> {left}, <strong>right:</strong> {right}
          {mid !== null && <> , <strong>mid:</strong> {mid}</>}
        </p>
      </div>
    </div>
  );
};

export default MatrixSearchAnimation;